import { gsap, Power2 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { useEffect, useRef, useState, useContext, memo } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import PropTypes from 'prop-types'

// Context
import { UserContext } from '../../context/UserContext'

// Utils
import getExerciseSvgImage from '../../utils/data/getExerciseSvgImage'
import getExercisePropertyData from '../../utils/data/getExercisePropertyData'
import getExercisePlaylistData from '../../utils/data/getExercisePlaylistData'
import getTrainingPropertyData from '../../utils/data/getTrainingPropertyData'
import getTrainingExerciseListData from '../../utils/data/getTrainingExerciseListData'
import getTrainingExercisePlaylistData from '../../utils/data/getTrainingExercisePlaylistData'
import getProgramPropertyData from '../../utils/data/getProgramPropertyData'
import getProgramObjectiveListData from '../../utils/data/getProgramObjectiveListData'
import getProgramFrequencyData from '../../utils/data/getProgramFrequencyData'

// Internal components
import AccountBadge from '../AccountBadge/AccountBadge'
import PropertyBlock from '../PropertyBlock/PropertyBlock'
import DetailBlock from '../DetailBlock/DetailBlock'
import LazyImage from '../LazyImage/LazyImage'
import Button from '../Button/Button'

gsap.registerPlugin(ScrollTrigger)

const DetailCard = memo((props) => {
  const { cardData, modifiers, cardIndex, direction, cardType, onSelectionConfirm } = props

  // Context
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // Refs
  const detailCardRef = useRef()
  const detailCardInnerRef = useRef()
  const detailCardListRef = useRef()
  const labelRef = useRef()
  const titleRef = useRef()
  const subTitleRef = useRef()
  const descriptionRef = useRef()
  const imageRef = useRef()

  // States
  const [timeline] = useState(() => gsap.timeline({ paused: true }))
  const [animationState, setAnimationState] = useState(false)
  const [mountedState, setMountedState] = useState(false)
  const [shownState, setShownState] = useState(false)
  const [modifierClasses, setModifierClasses] = useState('')

  const id = cardData.id
  const onMobile = window.innerWidth < 460
  const userCreated = cardData.userGenerated

  let typeHandle = cardData.elementHandle
  typeHandle = typeHandle === 'userExercise' ? 'exercise' : typeHandle
  typeHandle = typeHandle === 'userTraining' ? 'training' : typeHandle
  typeHandle = typeHandle === 'userProgram' ? 'program' : typeHandle

  let image = false
  let title = false
  let subtitle = false
  let label = false
  let list = false
  let description = false
  let playlist = false
  let propertyData = false
  let cardTypeModifier = false

  if (typeHandle === 'exercise') {
    title = cardData?.exerciseContent ? cardData?.exerciseContent[0].exerciseTitle : cardData?.title ?? false
    image = getExerciseSvgImage(cardData)
    propertyData = getExercisePropertyData(cardData, t, 'detailCard')
    playlist = cardType === 'calendar' ? getExercisePlaylistData(cardData) : false
    cardTypeModifier =
      cardData?.exerciseContent?.[0]?.fieldTypeCategory?.[0]?.title === 'indoor' ? ' DetailCard--indoor' : ''
  }

  if (typeHandle === 'training') {
    title = cardData.trainingTitle ? cardData.trainingTitle : false
    list = cardData.exercises ? getTrainingExerciseListData(cardData, 'detailCard') : false
    propertyData = getTrainingPropertyData(cardData, t, 'detailCard')
    playlist = cardType === 'calendar' ? getTrainingExercisePlaylistData(cardData) : false
  }

  if (typeHandle === 'program') {
    title = cardData.programTitle ? cardData.programTitle : false
    subtitle = getProgramFrequencyData(cardData, t)
    list = cardData.trainings ? getProgramObjectiveListData(cardData, t) : false
    propertyData = getProgramPropertyData(cardData, t, 'detailCard')
    const programIntroRaw =
      typeof cardData.programIntro === 'string' ? cardData.programIntro : cardData.programIntro?.html
    description = programIntroRaw
      ? programIntroRaw.includes('<p>')
        ? programIntroRaw
        : `<p>${programIntroRaw}</p>`
      : false
  }

  if (typeHandle === 'exercise' || typeHandle === 'training') {
    label =
      cardData?.objectiveCategories?.[0] && cardData?.objectiveCategories?.[0]?.level === 1
        ? cardData?.objectiveCategories?.[0]?.title
        : false
  }

  useEffect(() => {
    if (mountedState) return
    setAnimationProperties()
    return () => {
      if (detailCardRef.current) detailCardRef.current.classList.toggle('is-shown', false)
    }
  }, [])

  useEffect(() => {
    if (modifiers) setModifierClasses(modifiers ? ' ' + modifiers : '')
  }, [modifiers])

  useEffect(() => {
    if (animationState && detailCardRef.current && mountedState && !shownState) {
      setShownState(true)
      detailCardRef.current.classList.toggle('is-shown', true)
      ScrollTrigger.create({
        animation: timeline,
        trigger: detailCardRef.current,
        start: 'top center+=25%',
        end: 'bottom center-=25%'
      })
    }
  }, [animationState])

  const setAnimationProperties = () => {
    setMountedState(true)

    const listItemsRefs = detailCardListRef.current ? detailCardListRef.current.querySelectorAll('li') : false

    if (detailCardInnerRef.current && direction === 'vertical')
      timeline.from(detailCardInnerRef.current, {
        duration: onMobile ? 1 : 0.6,
        delay: onMobile ? 0 : cardIndex % 2 === 0 ? 0.1 : 0.15,
        y: onMobile ? 60 : 250,
        opacity: 0,
        ease: Power2.inOut
      })
    if (detailCardInnerRef.current && direction === 'horizontal')
      timeline.from(detailCardInnerRef.current, {
        duration: 0.8,
        delay: 0.3 + cardIndex * 0.06,
        x: 500,
        opacity: 0,
        ease: Power2.inOut
      })

    if (imageRef.current) timeline.from(imageRef.current, { duration: 1.4, scale: 1.7, ease: Power2.inOut }, '-=1')
    if (labelRef.current)
      timeline.from(
        labelRef.current,
        { duration: 0.4, y: 20, opacity: 0, ease: Power2.inOut },
        imageRef.current ? '-=0.9' : '-=0.5'
      )
    if (titleRef.current)
      timeline.from(
        titleRef.current,
        { duration: 0.4, y: 20, opacity: 0, ease: Power2.inOut },
        imageRef.current ? '-=0.8' : '-=0.4'
      )
    if (subTitleRef.current)
      timeline.from(
        subTitleRef.current,
        { duration: 0.4, y: 20, opacity: 0, ease: Power2.inOut },
        imageRef.current ? '-=0.8' : '-=0.4'
      )
    if (descriptionRef.current)
      timeline.from(
        descriptionRef.current,
        { duration: 0.4, y: 20, opacity: 0, ease: Power2.inOut },
        imageRef.current ? '-=0.8' : '-=0.4'
      )
    if (listItemsRefs)
      timeline.from(
        listItemsRefs,
        { duration: 0.4, y: 20, opacity: 0, stagger: 0.1, ease: Power2.inOut },
        imageRef.current ? '-=0.6' : '-=0.3'
      )

    setAnimationState(true)
  }

  const toggleDialog = (state) => {
    if (state)
      document.querySelectorAll('.DetailCard.has-dialog').forEach((card) => card.classList.toggle('has-dialog', false))
    detailCardRef.current.classList.toggle('has-dialog', state)
  }

  return (
    <div
      className={`DetailCard DetailCard--${typeHandle}${modifierClasses}${cardTypeModifier}${
        cardType === 'calendar' ? ' DetailCard--calendar' : ''
      }`}
      ref={detailCardRef}
    >
      <div className="DetailCard-inner" ref={detailCardInnerRef}>
        <Link href={`/${typeHandle}/id/[id]`} as={`/${typeHandle}/id/${id}`}>
          <a className="DetailCard-link">
            {image && (
              <div className="DetailCard-image" ref={imageRef}>
                <div className="DetailCard-imageWrap">
                  {typeHandle === 'exercise' && userCreated && (
                    <AccountBadge initials={userContext.userInitials} modifiers={['AccountBadge--detailCard']} />
                  )}
                  {typeHandle === 'exercise' && image && (
                    <LazyImage image={image} alt={image.alt} modifiers={['LazyImage--detailCard']} />
                  )}
                </div>
              </div>
            )}
            {(label || title || subtitle || description || list) && (
              <div className="DetailCard-content">
                {label && (
                  <span className="DetailCard-label" ref={labelRef}>
                    {label}
                  </span>
                )}
                {title && (
                  <span className="DetailCard-title" ref={titleRef}>
                    {title}
                  </span>
                )}
                {subtitle && (
                  <span className="DetailCard-subtitle" ref={subTitleRef}>
                    {subtitle}
                  </span>
                )}
                {description && (
                  <span className="DetailCard-description" ref={descriptionRef}>
                    <DetailBlock content={description} modifiers={['DetailBlock--detailCard']} />
                  </span>
                )}
                {list && (
                  <span className="DetailCard-list" ref={detailCardListRef}>
                    <DetailBlock data={list} modifiers={['DetailBlock--detailCard']} />
                  </span>
                )}
                {typeHandle !== 'exercise' && userCreated && (
                  <AccountBadge initials={userContext.userInitials} modifiers={['AccountBadge--detailCard']} />
                )}
              </div>
            )}
          </a>
        </Link>
        {cardType !== 'calendar' && (
          <>
            <div className="DetailCard-menu">
              <div className="DetailCard-propertyBlock">
                {propertyData && <PropertyBlock data={propertyData} modifiers={['ProperyBlock--detailCard']} />}
              </div>
              <div className="DetailCard-trigger">
                <Button
                  icon={{ id: 'icon-detailtrigger' }}
                  modifiers={['Button--detailCardTrigger']}
                  onClick={() => toggleDialog(true)}
                />
              </div>
            </div>
            <div className="DetailCard-dialog">
              <div className="DetailCard-dialogContent">
                <span className="DetailCard-dialogTitle">{t('DETAIL_CARD__DIALOG_TITLE')}</span>
                <div className="DetailCard-dialogButtonGroup">
                  {typeHandle === 'exercise' && (
                    <Button
                      label={t('DETAIL_CARD__DIALOG_BTN_TRAINING')}
                      modal={{ type: 'api', route: 'add/exercise-to-training', id: id }}
                      icon={{ id: 'icon-detailtrigger', before: true }}
                      modifiers={['Button--addDetailPage']}
                    />
                  )}
                  {typeHandle === 'training' && (
                    <>
                      <Button
                        label={t('DETAIL_CARD__DIALOG_BTN_CALENDAR')}
                        modal={{ type: 'api', route: 'add/training-to-date', id: id }}
                        icon={{ id: 'icon-detailtrigger', before: true }}
                        modifiers={['Button--addDetailPage']}
                      />
                      <Button
                        label={t('DETAIL_CARD__DIALOG_BTN_PROGRAM')}
                        modal={{ type: 'api', route: 'add/training-to-program', id: id }}
                        icon={{ id: 'icon-detailtrigger', before: true }}
                        modifiers={['Button--addDetailPage']}
                      />
                    </>
                  )}
                  {typeHandle === 'program' && (
                    <Button
                      label={t('DETAIL_CARD__DIALOG_BTN_CALENDAR')}
                      modal={{ type: 'api', route: 'add/program-to-date', id: id }}
                      icon={{ id: 'icon-detailtrigger', before: true }}
                      modifiers={['Button--addDetailPage']}
                    />
                  )}
                </div>
              </div>
              <div className="DetailCard-dialogTrigger">
                <Button
                  icon={{ id: 'icon-close' }}
                  modifiers={['Button--detailCardClose']}
                  onClick={() => toggleDialog(false)}
                />
              </div>
            </div>
          </>
        )}
        {cardType === 'calendar' && (
          <>
            <div className="DetailCard-menu">
              <div className="DetailCard-videoButton">
                {playlist && (
                  <Button
                    label={
                      typeHandle === 'training'
                        ? t('DETAIL_CARD__MENU_BTN_VIEW_EXERCISES')
                        : t('DETAIL_CARD__MENU_BTN_VIEW_VIDEO')
                    }
                    modal={{ type: 'gallery', id: id, playlist: playlist }}
                    icon={{ id: 'icon-play', before: true }}
                    modifiers={['Button--detailCardVideo']}
                  />
                )}
              </div>
              <div className="DetailCard-trigger">
                <Button
                  icon={{ id: 'icon-detailtrigger' }}
                  modifiers={['Button--detailCardTrigger']}
                  onClick={() => onSelectionConfirm({ id: id, title: title })}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
})

DetailCard.propTypes = {
  cardData: PropTypes.object,
  animationData: PropTypes.object,
  cardIndex: PropTypes.number,
  direction: PropTypes.string,
  cardType: PropTypes.string,
  modifiers: PropTypes.string,
  onSelectionConfirm: PropTypes.func,
  renderCount: PropTypes.number
}

export default DetailCard
